import { Box, Typography } from '@mui/material';
import { ReactNode, memo } from 'react';
import { useTranslation } from 'react-i18next';

import BallotIcon from 'shared/assets/icons/BallotOutlinedIcon.svg';
import { shouldShowUOM } from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { DynamicTooltip } from 'shared/ui/Tooltip';
import { UnitOfMeasureDisplay } from 'shared/ui/UnitOfMeasureDisplay/UnitOfMeasureDisplay';
import { decodeHtmlEntities } from 'shared/utils/textFormatters/textFormatters';

export interface CatalogItemProps {
    uom: string;
    brand: string;
    qtyPerUom: number;
    title: string;
    itemLabel: string;
    thumbnailUrl: string;
    isAvailable: boolean;
    isAddedToCatalog?: boolean;
    action: ReactNode;
    supplierSlot: ReactNode;
    priceSlot: ReactNode;
    onClickTitle: () => void;
}

export const CatalogItem = memo(
    ({
        uom,
        brand,
        qtyPerUom,
        title,
        itemLabel,
        thumbnailUrl,
        isAvailable,
        isAddedToCatalog = false,
        supplierSlot,
        priceSlot,
        action,
        onClickTitle,
    }: CatalogItemProps) => {
        const { t } = useTranslation();
        const { openLightbox } = useImageLightbox();

        const unavailableDimSx = isAvailable ? undefined : { opacity: 0.35 };

        return (
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '20px',
                    }}
                >
                    <Box
                        onClick={() => openLightbox(thumbnailUrl)}
                        sx={{
                            cursor: 'pointer',
                            position: 'relative',
                            width: '100px',
                            height: '100px',
                            ...unavailableDimSx,
                            ...(!isAvailable && {
                                backgroundColor: 'white',
                            }),
                            '&:hover': {
                                cursor: 'zoom-in',
                            },
                        }}
                    >
                        <CatalogItemImage
                            src={thumbnailUrl}
                            height="100px"
                            width="100px"
                            showBorder={false}
                        />
                        {itemLabel && (
                            <Typography
                                data-testclass="item-inventory-label"
                                typography="openSans.captionMedium"
                                color="black"
                                align="center"
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    backgroundColor:
                                        'rgba(255, 255, 255, 0.85)',
                                    py: '8px',
                                    fontSize: '12px',
                                }}
                            >
                                {itemLabel}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexGrow: 1,
                            flexDirection: 'column',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                minHeight: 0,
                                minWidth: 0,
                                ...unavailableDimSx,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                    alignSelf: 'flex-start',
                                    mb: '2px',
                                    cursor: 'pointer',
                                }}
                                onClick={onClickTitle}
                            >
                                {isAddedToCatalog && (
                                    <DynamicTooltip
                                        content={t('In property catalog')}
                                        enterDelay={200}
                                    >
                                        <img
                                            src={String(BallotIcon)}
                                            alt={t('In property catalog')}
                                            style={{
                                                boxSizing: 'content-box',
                                                width: '18px',
                                                height: '18px',
                                                paddingTop: '2px',
                                            }}
                                        />
                                    </DynamicTooltip>
                                )}

                                <Typography
                                    data-testclass="item-title"
                                    typography={'openSans.subtitle1Medium'}
                                    component="span"
                                >
                                    {decodeHtmlEntities(title)}
                                </Typography>
                            </Box>

                            <Box display="flex" flex="1" gap="20px">
                                {brand && (
                                    <Typography component="span">
                                        {decodeHtmlEntities(brand)}
                                    </Typography>
                                )}
                                {shouldShowUOM(uom) && (
                                    <UnitOfMeasureDisplay
                                        unitCode={uom}
                                        quantity={qtyPerUom}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Box>{supplierSlot}</Box>
                    </Box>

                    {action}
                </Box>
                <Box
                    sx={{
                        marginLeft: '120px',
                        paddingLeft: '8px',
                        ...unavailableDimSx,
                    }}
                >
                    {priceSlot}
                </Box>
            </Box>
        );
    },
);
